import forEachSelector from './elements/forEachSelector';
import addClickHandler from './events/addClickHandler';
import toggleClass from './elements/toggleClass';
import addPassiveEventListener from './events/addPassiveEventListener';
import isAncestorOf from 'node-is-ancestor-of';
import addStartupScript from './events/addStartupScript';
import 'element-closest-polyfill';
import mainNavigationTracking from './tracking/main_navigation_tracking';

const NAV                   = 'mc-nav';
const NAV_LEVEL_0           = `${NAV}--level-0`;
const NAV_ITEM              = `${NAV}-item`;
const NAV_ITEM_FOCUS        = `${NAV_ITEM}--focus`;
const NAV_ITEM_WITH_SUBNAV  = `${NAV_ITEM}--subnav`;
const NAV_ITEM__CONTENT     = `${NAV_ITEM}__content`;
const NAV_SUBNAV            = `${NAV}-subnav`;
const NAV_SUBNAV_OPEN       = `${NAV_SUBNAV}--open`;
const NAV_SUBNAV__BACK      = `${NAV_SUBNAV}__back`;
const NAVBAR                = 'mc-navbar';
const NAVBAR__NAV           = `${NAVBAR}__nav`;

/**
 * Finds the root level nav for the given element
 * @param {Element} el
 * @returns {Element|null}
 */
const findRootMenu = el =>
  el.closest(`.${NAV_LEVEL_0}`);

/**
 * Finds the containing navbar for the given element
 * @param {Element} el
 * @returns {Element|null}
 */
const findRootNavbar = el =>
  el.closest(`.${NAVBAR__NAV}`);

/**
 * True if the given element is currently open/focused
 * @param {Element} el - Navitem or Subnav
 * @returns {boolean}
 */
const isDropdownOpen = (el) =>
  el.classList.contains(NAV_ITEM_FOCUS) ||
  el.classList.contains(NAV_SUBNAV_OPEN);

/**
 * True if we are in dropdown rendering mode (as opposed to slide-in mobile drawer), which can
 * be klugingly calculated by checking if any subnavs have "will-change" set in CSS,
 * because they are dropdowns.
 */
const isDropDownMode = () =>
  Array.from(document.querySelectorAll(`.${NAV_SUBNAV}`)).some(
    (subnav) => getComputedStyle(subnav)['will-change'] === 'transform'
  );

/**
 * Checks if the opening dropdown might extend off the right side of the window, and if so,
 * updates its right margin so that it can be repositioned.
 * Also toggles the 'open' class if the nav item is focused.
 * @param {Element} navItem - The nav item to check.
 */
export function updateSubnavPosition(navItem) {
  const subnav = navItem.querySelector(`.${NAV_SUBNAV}`);

  if (subnav) {
    toggleClass(
      subnav,
      NAV_SUBNAV_OPEN,
      navItem.classList.contains(NAV_ITEM_FOCUS)
    );

    if (navItem.classList.contains(NAV_ITEM_FOCUS)) {
      if (isDropDownMode()) {
        const box = subnav.getBoundingClientRect();
        const { innerWidth } = subnav.ownerDocument.defaultView;
        const marginLeft = Math.min(
          0,
          parseInt(innerWidth) - box.right - 10 + parseInt(getComputedStyle(subnav).marginLeft || 0)
        );
        subnav.style.marginLeft = marginLeft + 'px';
      } else {
        // mobile drawer mode
        subnav.style.marginLeft = '';
        document.querySelector(`.${NAVBAR__NAV}`).scrollTop = 0;
      }
    }
  }
}

function setElementCurrentLevel(el, prefix, level) {
  for (let i = 1; i < 5; i++) {
    if (i === level) {
      el.classList.add(prefix + '--current-level-' + i);
    } else {
      el.classList.remove(prefix + '--current-level-' + i);
    }
  }
}

/**
 * Updates the root menu and navbar for the current nav item to the given level
 * @param {Element} el - The element whose root nav to query
 * @param [level] - The numeric level to set to, or nullish
 */
function updateRootCurrentLevel(el, level) {
  const root = findRootMenu(el);
  const navbar = findRootNavbar(el);
  if (root) setElementCurrentLevel(root, 'mc-nav', level);
  if (navbar) setElementCurrentLevel(navbar, 'mc-navbar-nav', level);
}

/**
 * Toggles the open state of the given dropdown nav item.
 * @param {Element} navItem - The navItem to open or close.
 * @param {Boolean} [toggled] - True or false to force the dropdown open
 */
function toggleDropdown(navItem, toggled) {
  toggleClass(navItem, NAV_ITEM_FOCUS, toggled);
  updateSubnavPosition(navItem);
}

/**
 * Removes the mc-nav-item--open from all dropdowns, except the optional given element.
 * @param {Element} [activeNavItem] - The navItem to keep open. Optional.
 */
function toggleAllDropdowns(activeNavItem) {
  forEachSelector(`.${NAV_ITEM}`, (navItem) => {
    const toggled = !!activeNavItem && isAncestorOf(navItem, activeNavItem);
    toggleDropdown(navItem, toggled);
  });
}

/**
 * Called whenever a user clicks on a Dropdown error.
 * @param {Event} event - The click event.
 */
function handleDropdownClick(event) {
  const item = this.closest(`.${NAV_ITEM}`);
  if (item?.classList.contains(NAV_ITEM_WITH_SUBNAV)) {
    event.preventDefault();
    event.stopPropagation();
    toggleAllDropdowns(isDropdownOpen(item) ? null : item);
    updateRootCurrentLevel(item, isDropdownOpen(item) ? parseInt(item.dataset.level) + 1 : 0);
  }
}

/**
 * Called whenever a user clicks on a nav back link.
 * @param {Event} event - The click event.
 */
function handleBackClick(event) {
  const item = this.closest(`.${NAV_ITEM}`);
  event.preventDefault();
  event.stopPropagation();
  if (parseInt(item.dataset.level)) {
    toggleAllDropdowns(item.parentNode.closest(`.${NAV_ITEM}`));
  } else {
    toggleAllDropdowns();
  }
  updateRootCurrentLevel(item, parseInt(item.dataset.level));
}

/**
 * Called when the user clicks anywhere outside a Nav dropdown.
 * @param {Event} event - The click event
 */
function handleOutsideDropdownClick(event) {
  if (!event.target.closest(`.${NAV_ITEM_WITH_SUBNAV}`)) {
    toggleAllDropdowns();
    forEachSelector(`.${NAV}`, (el) => updateRootCurrentLevel(el, 0));
  }
}

function handleNavClick(event) {
  if (swiping) return;

  const navItemContent = event.target.closest(`.${NAV_ITEM__CONTENT}`);
  const backButton = event.target.closest(`.${NAV_SUBNAV__BACK}`);

  if (backButton) handleBackClick.call(backButton, event);
  if (navItemContent) {
    handleDropdownClick.call(navItemContent, event);
    mainNavigationTracking(event.target);
  }
}

addClickHandler(window, handleOutsideDropdownClick);
addClickHandler(document, handleNavClick);

// invoke touchend only if user is not swiping up and down
let swiping = false;
document.addEventListener('touchstart', () => { swiping = false; }, false);
document.addEventListener('touchmove', () => { swiping = true; }, false);
document.addEventListener('touchend', handleNavClick, false);
addStartupScript(function setup() {
  const navbarNav = document.querySelector('.mc-navbar__nav');
  navbarNav && addPassiveEventListener(navbarNav, 'scroll', () => { swiping = true; });
});

// Reposition currently open dropdown menu while resizing window
addPassiveEventListener(window, 'resize', function () {
  const openNav = document.querySelector(`.${NAV_SUBNAV_OPEN}`);
  if (openNav) updateSubnavPosition(openNav.parentNode);
});
